// useOwnerRequests hook — CRUD operations for owner requests
import { useState, useEffect, useCallback } from "react";
import { api, getApiErrorMessage } from "@/services/api";
import type {
  OwnerRequest,
  CreateOwnerRequestPayload,
  UpdateOwnerRequestPayload,
  PaginatedResponse,
} from "@/types";

interface UseOwnerRequestsReturn {
  requests: OwnerRequest[];
  isLoading: boolean;
  error: string | null;
  total: number;
  refresh: () => Promise<void>;
  createRequest: (payload: CreateOwnerRequestPayload) => Promise<OwnerRequest>;
  updateRequest: (id: string, payload: UpdateOwnerRequestPayload) => Promise<OwnerRequest>;
  deactivateRequest: (id: string) => Promise<void>;
}

export function useOwnerRequests(status?: string): UseOwnerRequestsReturn {
  const [requests, setRequests] = useState<OwnerRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const query: Record<string, string | number | undefined> = { limit: 50 };
      if (status) query.status = status;
      const result = await api.get<PaginatedResponse<OwnerRequest>>("/owner-requests", query);
      setRequests(result.data);
      setTotal(result.total);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  }, [status]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const createRequest = async (payload: CreateOwnerRequestPayload): Promise<OwnerRequest> => {
    const result = await api.post<OwnerRequest>("/owner-requests", payload);
    await refresh();
    return result;
  };

  const updateRequest = async (id: string, payload: UpdateOwnerRequestPayload): Promise<OwnerRequest> => {
    const result = await api.patch<OwnerRequest>(`/owner-requests/${id}`, payload);
    await refresh();
    return result;
  };

  const deactivateRequest = async (id: string): Promise<void> => {
    await api.delete(`/owner-requests/${id}`);
    await refresh();
  };

  return {
    requests,
    isLoading,
    error,
    total,
    refresh,
    createRequest,
    updateRequest,
    deactivateRequest,
  };
}
